import { Component, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Breadcrumb } from '@app/models/_base/breadcrumb.model';
import { Row } from '@app/models/_shared/column.model';
import { CourseAreaDetail } from '@app/models/courses/courseAreaDetail.model';
import { DetailSection } from '@app/models/courses/detailSection.model';
import { CourseService } from '@app/services/course.service';
import { Footer } from '@app/services/footer.service';
import { LocalisationService } from '@app/services/localisation.service';
import { QuestionnaireService } from '@app/services/questionnaire.service';
import { RouteBreadcrumbs } from '@app/services/routeBreadcrumbs.service';
import { RowColumnSorter } from '@app/services/row-column-sorter.service';
import { GraphDataset } from 'app/models/_base/graphDataset.model';
import { ToastrService } from 'ngx-toastr';
import { UserService } from '@app/services/user.service';

import { CourseAreaListComponent } from './../../list/course-area-list/course-area-list.component';
import { LoadingProvider } from '@app/components/_shared/loading-spinner/loading-spinner.component';
import { DcdArea } from '@app/models/dcd/area.model';
import { BtnDropdownComponent } from '@appShared/components/btn-dropdown/btn-dropdown.component';
import { ModalProvider } from '@app/components/_shared/modal/modal.component';

@Component({
  selector: 'app-course-area-detail',
  templateUrl: './course-area-detail.component.html',
  styleUrls: ['./course-area-detail.component.scss']
})
export class CourseAreaDetailComponent implements OnInit {
  public static route: string = 'course-areas/detail/:id';

  @ViewChild(BtnDropdownComponent)
  child;

  locale: any = {};
  parentLocale: any = {};

  id: number;

  details: CourseAreaDetail;
  matchSection: DetailSection = new DetailSection();
  chartSection: DetailSection;

  textCareers: DetailSection[];
  textCourses: DetailSection[];
  textEntrance: DetailSection[];
  textWebsites: DetailSection[];
  textReading: DetailSection[];
  textCautionary: DetailSection[];

  textSectionRows: Row<DetailSection>[] = [];

  numberOfSelectedCourses: number = 0;
  graphReady: boolean = false;
  doughnutChartLabels: string[] = [];
  doughnutChartType: string = 'doughnut';
  datasets: GraphDataset[] = [];
  options: any = {};
  chartColors: any[] = ['#57BCBC', '#454F5D', '#C8CACC'];
  relatedDcd: DcdArea;
  dcdLoaded: boolean = false;
  public formSubmitted: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private routeBreadcrumbs: RouteBreadcrumbs,
    private localisation: LocalisationService,
    private courseService: CourseService,
    private footerService: Footer,
    private questionnaireService: QuestionnaireService,
    private rowColumnSorter: RowColumnSorter,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private spinner: LoadingProvider,
    private modal: ModalProvider,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.locale = this.localisation.getTranslationsForComponent('CourseAreaDetailComponent');
    this.spinner.show(this.locale.loadingDetail, true);

    this.parentLocale = this.localisation.getTranslationsForComponent(
      'CourseAreaListComponent'
    ) as any;

    this.route.params.subscribe(params => {
      this.id = +params['id'];

      this.loadDetail();

      this.loadSelectedAreas();
    });

    this.routeBreadcrumbs.breadcrumbs.next([
      new Breadcrumb(this.parentLocale.breadcrumbText, CourseAreaListComponent.route),
      new Breadcrumb(this.locale.breadcrumbText, null)
    ]);

    this.footerService.update({
      title: this.locale.footerText,
      action: CourseAreaListComponent.route,
      alignLeft: true
    });
  }

  clickedItem(data) {
    const clickedArticle = this.relatedDcd.articles.find(article => article.id === data.item.id);

    this.courseService.goToDcdArticle(clickedArticle.id);
  }

  loadDetail() {
    this.spinner.show(this.locale.loadingDetail, true);

    this.courseService.getAreaRelatedDcds(this.id).subscribe(response => {
      this.relatedDcd = response.data;
      this.dcdLoaded = true;
    });

    this.courseService
      .getCourseAreaDetail(this.id)
      .finally(() => {
        this.spinner.hide();
      })
      .subscribe(
        details => {
          this.details = details;

          this.details.safeImage = this.sanitizer.bypassSecurityTrustStyle(
            `url(${this.details.image})`
          );

          this.routeBreadcrumbs.breadcrumbs.next([
            new Breadcrumb(this.parentLocale.breadcrumbText, CourseAreaListComponent.route),
            new Breadcrumb(details.title, null)
          ]);

          this.matchSection = this.details.sections.find(sec => sec.type == 'DTYPE_MATCH');
          this.textCautionary = this.details.sections.filter(sec => sec.type == 'DTYPE_CAUTIONARY');
          this.textCareers = this.details.sections.filter(sec => sec.type == 'DTYPE_CAREERS');
          this.textCourses = this.details.sections.filter(sec => sec.type == 'DTYPE_COURSES');
          this.textEntrance = this.details.sections.filter(sec => sec.type == 'DTYPE_ENTRANCE');
          this.textWebsites = this.details.sections.filter(sec => sec.type == 'DTYPE_WEBSITES');
          this.textReading = this.details.sections.filter(sec => sec.type == 'DTYPE_READING');
          this.chartSection = this.details.sections.find(sec => sec.type == 'DTYPE_PIE');

          if (this.chartSection) this.generateChart();
        },
        err => {}
      );
  }

  loadSelectedAreas() {
    this.courseService.getCourseAreas().subscribe(response => {
      this.numberOfSelectedCourses = response.courseAreas.filter(
        courseArea => courseArea.selected
      ).length;

      if (this.numberOfSelectedCourses <= 0) {
        this.questionnaireService.statusOverride.next(100);
      }
    });
  }

  setFormSubmitted(value) {
    this.formSubmitted = value;
  }

  toggleSelected(): void {
    this.setFormSubmitted(true);
    if (this.details.selected) {
      this.courseService
        .deselectCourseArea(this.details.id)
        .finally(() => {
          this.setFormSubmitted(false);
        })
        .subscribe(
          result => {
            this.details.selected = false;
            this.numberOfSelectedCourses--;
            if (this.numberOfSelectedCourses <= 0) {
              this.questionnaireService.statusOverride.next(100);
            }
          },
          error => {
            this.toastr.error(this.locale.maxSelectedMessage, this.locale.maxSelectedTitle);
          }
        );
    } else {
      this.courseService
        .selectCourseArea(this.details.id)
        .finally(() => {
          this.setFormSubmitted(false);
        })
        .subscribe(
          result => {
            this.details.selected = true;
            this.questionnaireService.statusOverride.next(300);
          },
          error => {
            this.toastr.error(this.locale.maxSelectedMessage, this.locale.maxSelectedTitle);
          }
        );
    }
  }

  generateChart() {
    this.options = {
      title: {
        text: '*' + this.chartSection.keyHeader
      },
      tooltips: {
        callbacks: {
          //This adds the percentage sign to the labels.
          label: function(tooltipItem, data) {
            //get the concerned dataset
            var dataset = data.datasets[tooltipItem.datasetIndex];

            //get the current items value
            var currentValue = dataset.data[tooltipItem.index];
            var currentLabel = dataset.label[tooltipItem.index];
            return currentLabel + ': ' + currentValue + '%';
          }
        }
      }
    };

    let values: number[] = [];
    let labels: string[] = [];

    this.chartSection.data.forEach(data => {
      values.push(data.value);
      labels.push(data.key);
    });

    this.doughnutChartLabels = labels;
    this.datasets = [new GraphDataset(values, labels, this.chartColors, this.chartColors)];

    this.graphReady = true;
  }

  chartClicked(e: any): void {}

  chartHovered(e: any): void {}
}



// WEBPACK FOOTER //
// ./src/app/components/course-areas/detail/course-area-detail/course-area-detail.component.ts